<script setup>
import { ref, reactive, computed, onMounted, watch, provide, nextTick } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import { criarConfigBuscaCategoriaFinanceira } from '@/search-definitions/categoriaFinanceiraSearch'
import { dataHojeBR, formatarDataBR } from '@/utils/useDataBR'
import { ST } from '@/constants/ST'
import { FIN_ST_PLAN_CATEGFIN } from '@/constants/FIN_ST_PLAN_CATEGFIN'

import { criarConfigBuscaContaBancaria } from '@/search-definitions/contaBancariaBuscaSearch'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  tituloFinanceiro: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'salvo', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const isLoading = ref(false)
const isLoadingListas = ref(false)
const listas = reactive({})

// ============================================================
// VALIDAÇÃO FRONTEND (provide/inject para componentes base)
// ============================================================
const formErrors = ref({})
provide('formErrors', formErrors)

// Plano de categoria financeira vigente para filtro da busca
const idPlanoCategoriaFinanceira = ref(null)

/**
 * Converte uma string de data (DD/MM/AAAA ou YYYY-MM-DD) para Date
 */
const parseDataParaDate = (dataStr) => {
  if (!dataStr) return null
  // Formato ISO YYYY-MM-DD (vindo de input type="date")
  const isoMatch = dataStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (isoMatch) {
    return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]))
  }
  // Formato BR DD/MM/AAAA
  const brMatch = dataStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (brMatch) {
    return new Date(parseInt(brMatch[3]), parseInt(brMatch[2]) - 1, parseInt(brMatch[1]))
  }
  return null
}

/** Resolve o plano de categoria financeira ativo e vigente para a data de pagamento */
const resolverPlanoCategoria = async () => {
  try {
    const res = await api.get('/PlanosCategoriasFinanceiras', {
      params: {
        lstStatusPlano: FIN_ST_PLAN_CATEGFIN.Ativo,
        idEmpresa: authStore.empresaAtiva,
        pageSize: 50
      }
    })
    const dataPagamento = item.dataPagamento || dataHojeBR()
    const dataPagamentoObj = parseDataParaDate(dataPagamento)
    if (!dataPagamentoObj) {
      console.warn('[BaixaTituloFinanceiro] Formato de data inválido:', dataPagamento)
      idPlanoCategoriaFinanceira.value = null
      return
    }
    const plano = (res.data || []).find(p => {
      if (!p.dataInicioVigencia || !p.dataFimVigencia) return false
      const inicio = new Date(p.dataInicioVigencia + 'T00:00:00')
      const fim = new Date(p.dataFimVigencia + 'T00:00:00')
      return dataPagamentoObj >= inicio && dataPagamentoObj <= fim
    })
    idPlanoCategoriaFinanceira.value = plano ? plano.idPlanoCategoriaFinanceira : null
    if (!idPlanoCategoriaFinanceira.value) {
      console.warn('[BaixaTituloFinanceiro] Nenhum plano ativo vigente encontrado para a data:', dataPagamento)
    }
  } catch (e) {
    console.warn('[BaixaTituloFinanceiro] Erro ao resolver plano vigente:', e)
    idPlanoCategoriaFinanceira.value = null
  }
}

const item = reactive({
  idContaBancaria: null,
  nomeContaBancaria: '',
  dataMovimento: dataHojeBR(),
  valorEfetivoMoedaBanco: null,
  valorAbatidoTitulo: null,
  valorDescontoMoedaTitulo: null,
  valorDescontoFinanceiroMoedaTitulo: null,
  lstFormaPagamento: ''
})

// Dados compilados da conta bancária selecionada
const dadosContaBancaria = reactive({
  apelido: '',
  banco: '',
  agencia: '',
  conta: '',
  moeda: ''
})

// Toggle do modal
const abaterDivida = ref(false)

// Quando ativa "Abater Dívida", limpa e bloqueia a Categoria Financeira
watch(abaterDivida, (novoValor) => {
  if (novoValor) {
    modalItemParaEdicao.idCategoriaFinanceira = null
    modalItemParaEdicao.nomeCategoriaFinanceira = ''
    modalItemParaEdicao.codigoEstruturado = ''
  }
})

// Se já tem item fake no grid
const jaTemItemFake = computed(() => itensBaixa.value.some(i => i.isFake))

// Se a lista está vazia, o primeiro lançamento é sempre abatimento de dívida
const primeiroItem = computed(() => itensBaixa.value.length === 0)

// Se moeda da conta é igual moeda do título
const moedasIguais = computed(() => {
  return dadosContaBancaria.moeda && props.tituloFinanceiro?.lstMoeda
    ? String(dadosContaBancaria.moeda) === String(props.tituloFinanceiro.lstMoeda)
    : false
})

// Percentual da barra de progresso (percentual real, sem cap em 100%)
const percentualComposicao = computed(() => {
  const total = Number(item.valorEfetivoMoedaBanco) || 0
  if (total <= 0) return 0
  return Math.round((totalItensMoedaBanco.value / total) * 100)
})

// Flag: total da composição excede o Valor Movimentado
const excedeValorMovimentado = computed(() => {
  const totalMovimentado = Number(item.valorEfetivoMoedaBanco) || 0
  return totalItensMoedaBanco.value > totalMovimentado
})

// Título info (readonly)
const tituloInfo = computed(() => {
  if (!props.tituloFinanceiro) return null
  return props.tituloFinanceiro
})

// Lookup do texto amigável do tipo de título via lista
const tipoTituloTexto = computed(() => {
  const codigo = props.tituloFinanceiro?.lstTipoTitulo
  if (!codigo) return '---'
  const item = (listas.LstTipoTitulo || []).find(m => String(m.idListaSistemaItem) === String(codigo))
  return item ? item.textoLista : codigo
})

// Função de lookup de moeda (padrão do projeto)
const getMoedaIso = (idMoeda) => {
  if (!idMoeda) return 'MOEDA'
  const moedaObj = (listas.LstMoeda || []).find(m => String(m.idListaClienteItem) === String(idMoeda) || m.textoLista === idMoeda)
  const texto = moedaObj ? moedaObj.textoLista : idMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : 'MOE'
}

// Sigla ISO da moeda do título para labels dinâmicos
const siglaMoedaTitulo = computed(() => {
  const codigo = props.tituloFinanceiro?.lstMoeda
  if (!codigo) return '' // Era Moeda Título
  return getMoedaIso(codigo)
})

// Sigla ISO da moeda da conta bancária selecionada (resolve o código cru da lista)
const siglaMoedaConta = computed(() => {
  const codigo = dadosContaBancaria.moeda
  if (!codigo) return '' // Era Moeda Conta
  return getMoedaIso(codigo)
})

// ============================================================
// Split Payment — tributos previstos para gerar na liquidação
// ============================================================
const tributosLiquidacao = ref([])

const carregarTributosLiquidacao = async () => {
  tributosLiquidacao.value = []
  const idTitulo = props.tituloFinanceiro?.idTituloFinanceiro
  if (!idTitulo) return
  try {
    const res = await api.get(`/TitulosFinanceiros/${idTitulo}/tributos-liquidadacao`, { _suppressLoading: true })
    tributosLiquidacao.value = res.data || []
  } catch (e) {
    console.warn('[BaixaTituloFinanceiro] Erro ao carregar tributos de liquidação:', e)
    tributosLiquidacao.value = []
  }
}

const totalTributosLiquidacao = computed(() =>
  (tributosLiquidacao.value || []).reduce((acc, t) => acc + (Number(t.valorAArrecadar) || 0), 0)
)

/** Resolve o texto amigável do Tipo de Tributo (TRIBUTO_TP) via metadata. */
const getTributoTexto = (codigo) => {
  if (!codigo) return '---'
  const itens = listas.LstTipoTributo || []
  const found = itens.find(i =>
    String(i.idListaClienteItem ?? i.idListaSistemaItem ?? i.id) === String(codigo)
  )
  return found ? (found.textoLista || found.descricaoItem || codigo) : codigo
}

watch(() => props.isOpen, async (open) => {
  if (open && props.tituloFinanceiro) {
    await carregarListas()
    item.dataPagamento = dataHojeBR()
    await resolverPlanoCategoria()
    itensBaixa.value = []
    dadosContaBancaria.apelido = ''
    dadosContaBancaria.banco = ''
    dadosContaBancaria.agencia = ''
    dadosContaBancaria.conta = ''
    dadosContaBancaria.moeda = ''
    item.lstFormaPagamento = props.tituloFinanceiro.lstFormaPagamento || ''
    await carregarTributosLiquidacao()
  }
})

// Fallback: garante que o plano seja resolvido no onMounted se o modal já estiver aberto
onMounted(async () => {
  if (props.isOpen && props.tituloFinanceiro && !idPlanoCategoriaFinanceira.value) {
    item.dataPagamento = dataHojeBR()
    await resolverPlanoCategoria()
  }
})

// Configuração de Busca de Conta Bancária (enhanced)
const configBuscaConta = computed(() => ({
  ...criarConfigBuscaContaBancaria(authStore.organizacaoAtiva),
  colunasGrid: [
    { header: 'ID', dataField: 'idContaBancaria', width: '8%' },
    { header: 'Apelido', dataField: 'descricao', width: '25%' },
    { header: 'Banco', dataField: 'nomeInstituicaoFinanceira', width: '22%' },
    { header: 'Agência', dataField: 'agencia', width: '15%' },
    { header: 'Conta', dataField: 'numeroConta', width: '15%' },
    { header: 'Moeda', dataField: 'lstMoeda', width: '15%', formatter: (value) => getMoedaIso(value) }
  ]
}))

// Configuração de Busca de Categoria Financeira (para itens)
// Filtra automaticamente conforme o tipo do título via CATFIN_TP_MOV_CUSTOM.
// APagar → grupo Despesa (Custo, Despesa, Imposto, N/A)
// AReceber → grupo Receita (Receita, N/A)
const configBuscaCategoria = ref(null)

// Atualiza a config sempre que as dependências mudarem
const atualizarConfigBuscaCategoria = () => {
  configBuscaCategoria.value = criarConfigBuscaCategoriaFinanceira(
    'Pesquisar Categoria Financeira',
    null,
    ST.Ativo,
    authStore.organizacaoAtiva,
    props.tituloFinanceiro?.lstTipoTitulo,
    idPlanoCategoriaFinanceira.value
  )
}

watch([() => authStore.organizacaoAtiva, () => props.tituloFinanceiro?.lstTipoTitulo, idPlanoCategoriaFinanceira], atualizarConfigBuscaCategoria, { immediate: true })

// Carregamento de listas
const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['MovimentacaoBancaria', 'TituloFinanceiro', 'TributoConfiguracao'])
    Object.assign(listas, result)

    if (!listas.LstFormaPagamento) {
      try {
        const res = await api.get('/ListasSistema/lookup/FIN_FORMAS_PG')
        listas.LstFormaPagamento = res.data
      } catch (e) { console.warn('FIN_FORMAS_PG não encontrada') }
    }
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados auxiliares.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

// ============================================================
// VALIDAÇÃO FRONTEND (formErrors via provide/inject)
// ============================================================
const validarCamposObrigatoriosFrontEnd = () => {
  for (const key in formErrors.value) {
    delete formErrors.value[key]
  }

  let isValid = true
  const labelsObrigatorios = document.querySelectorAll('label.is-required')

  labelsObrigatorios.forEach(label => {
    if (label.offsetParent === null) return

    const inputId = label.getAttribute('for')
    if (!inputId) return

    const inputElement = document.getElementById(inputId)
    if (!inputElement) return

    let isEmpty = false
    if (inputElement.tagName === 'SELECT') {
      isEmpty = !inputElement.value || inputElement.value === ''
    } else {
      isEmpty = !inputElement.value || inputElement.value.trim() === ''
    }

    if (isEmpty) {
      const nomeCampoLimpo = label.innerText.replace('*', '').trim()
      formErrors.value[inputId] = `O campo "${nomeCampoLimpo}" é de preenchimento obrigatório.`
      isValid = false
    }
  })

  return isValid
}

const focarPrimeiroErro = async () => {
  await nextTick()
  setTimeout(() => {
    const elementoComErro = document.querySelector('.is-invalid')
    if (elementoComErro) {
      elementoComErro.focus()
      elementoComErro.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, 50)
}

// --- ITENS DA MOVIMENTAÇÃO (MovimentacaoBancariaItem) ---
const itensBaixa = ref([])
const isModalItemOpen = ref(false)
const indiceItemEditando = ref(-1)

const novoItemModelo = {
  idCategoriaFinanceira: null,
  nomeCategoriaFinanceira: '',
  codigoEstruturado: '',
  valorMoedaBanco: null,
  valorMoedaPadrao: 0,
  valorMoedaGerencial: null,
  descricaoComplementar: '',
  isFake: false // NOVO
}

const modalItemParaEdicao = reactive({ ...novoItemModelo })

// Computed totals
const totalItensMoedaBanco = computed(() => {
  return itensBaixa.value.reduce((acc, cur) => acc + (Number(cur.valorMoedaBanco) || 0), 0)
})

const totalItensMoedaBancoExcluindoEditando = computed(() => {
  return itensBaixa.value.reduce((acc, cur, index) => {
    if (index === indiceItemEditando.value) return acc
    return acc + (Number(cur.valorMoedaBanco) || 0)
  }, 0)
})

const abrirModalItem = (index = -1) => {
  // Limpa erros anteriores do modal interno
  delete formErrors.value.modalValorRateio
  delete formErrors.value.idCategoriaFinanceira

  // Garante que a config de busca esteja atualizada antes de abrir o modal
  atualizarConfigBuscaCategoria()

  if (index === -1) {
    // Primeiro item da lista é sempre abatimento de dívida
    abaterDivida.value = itensBaixa.value.length === 0
    indiceItemEditando.value = -1
    Object.assign(modalItemParaEdicao, novoItemModelo)
  } else {
    indiceItemEditando.value = index
    Object.assign(modalItemParaEdicao, itensBaixa.value[index])
    if (itensBaixa.value[index]?.isFake) {
      abaterDivida.value = true
    }
  }
  isModalItemOpen.value = true
}

const salvarModalItem = () => {
  // Limpa erros específicos do modal interno
  delete formErrors.value.modalValorRateio
  delete formErrors.value.idCategoriaFinanceira

  let isValid = true

  // Validação do Valor (obrigatório sempre)
  if (!modalItemParaEdicao.valorMoedaBanco || modalItemParaEdicao.valorMoedaBanco <= 0) {
    formErrors.value.modalValorRateio = 'O campo "Valor" é de preenchimento obrigatório.'
    isValid = false
  }

  // Validação da Categoria Financeira (obrigatória apenas quando não abater dívida)
  if (!abaterDivida.value && !modalItemParaEdicao.idCategoriaFinanceira) {
    formErrors.value.idCategoriaFinanceira = 'O campo "Categoria Financeira" é de preenchimento obrigatório.'
    isValid = false
  }

  if (!isValid) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios.', type: 'warning' })
    focarPrimeiroErro()
    return
  }

  const totalAposLancamento = totalItensMoedaBancoExcluindoEditando.value + Number(modalItemParaEdicao.valorMoedaBanco)
  if (totalAposLancamento > Number(item.valorEfetivoMoedaBanco)) {
    emit('show-notification', {
      message: `O valor total lançado (${totalAposLancamento.toFixed(2)}) excede o Valor Movimentado (${Number(item.valorEfetivoMoedaBanco).toFixed(2)}).`,
      type: 'warning'
    })
    return
  }

  if (abaterDivida.value) {
    modalItemParaEdicao.idCategoriaFinanceira = -1
    modalItemParaEdicao.nomeCategoriaFinanceira = 'Principal - Amortização da Dívida'
    modalItemParaEdicao.codigoEstruturado = ''
    modalItemParaEdicao.isFake = true
  } else {
    modalItemParaEdicao.isFake = false
  }

  const payloadItem = { ...modalItemParaEdicao }

  if (indiceItemEditando.value === -1) {
    itensBaixa.value.push(payloadItem)
  } else {
    itensBaixa.value.splice(indiceItemEditando.value, 1, payloadItem)
  }

  isModalItemOpen.value = false
  abaterDivida.value = false
}

const replicarValorRestante = () => {
  const total = Number(item.valorEfetivoMoedaBanco) || 0
  const usado = totalItensMoedaBanco.value
  const restante = Math.max(0, total - usado)
  modalItemParaEdicao.valorMoedaBanco = restante > 0 ? restante : total
}
const replicarSaldoDevedor = () => {
  item.valorAbatidoTitulo = props.tituloFinanceiro?.saldoDevedor || null
}

const replicarSaldoDevedorMovimentado = () => {
  item.valorEfetivoMoedaBanco = props.tituloFinanceiro?.saldoDevedor || null
}

// Recalcula "Valor a abater da dívida" como soma do item fake (amortização) + descontos
const recalcularValorAbater = () => {
  if (!moedasIguais.value) return
  const itemFake = itensBaixa.value.find(i => i.isFake)
  const amortizacao = Number(itemFake?.valorMoedaBanco) || 0
  const descComercial = Number(item.valorDescontoMoedaTitulo) || 0
  const descFinanceiro = Number(item.valorDescontoFinanceiroMoedaTitulo) || 0
  item.valorAbatidoTitulo = amortizacao + descComercial + descFinanceiro
}

watch(totalItensMoedaBanco, recalcularValorAbater)

watch([() => item.valorDescontoMoedaTitulo, () => item.valorDescontoFinanceiroMoedaTitulo], () => {
  if (moedasIguais.value) {
    recalcularValorAbater()
  }
})

const removerItem = (index) => {
  const item = itensBaixa.value[index]
  if (item?.isFake) return
  itensBaixa.value.splice(index, 1)
}

const confirmarBaixa = async () => {
  // Validação frontend obrigatória (formErrors via provide/inject)
  if (!validarCamposObrigatoriosFrontEnd()) {
    await focarPrimeiroErro()
    const primeiraMensagem = Object.values(formErrors.value)[0] || 'Preencha todos os campos obrigatórios.'
    emit('show-notification', { message: primeiraMensagem, type: 'warning' })
    return
  }

  // Validações específicas de negócio
  if (!item.valorEfetivoMoedaBanco || Number(item.valorEfetivoMoedaBanco) <= 0) {
    emit('show-notification', { message: 'O Valor Movimentado deve ser maior que zero.', type: 'warning' })
    return
  }

  if (!item.valorAbatidoTitulo || Number(item.valorAbatidoTitulo) <= 0) {
    emit('show-notification', { message: 'Valor a abater da dívida é obrigatório.', type: 'warning' })
    return
  }

  if (excedeValorMovimentado.value) {
    emit('show-notification', {
      message: `O valor total da Composição do Movimento (${totalItensMoedaBanco.value.toFixed(2)}) excede o Valor Movimentado (${Number(item.valorEfetivoMoedaBanco).toFixed(2)}). Corrija os valores antes de salvar.`,
      type: 'warning'
    })
    return
  }

  if (totalItensMoedaBanco.value !== Number(item.valorEfetivoMoedaBanco)) {
    emit('show-notification', {
      message: `A Composição do Movimento deve totalizar ${Number(item.valorEfetivoMoedaBanco).toFixed(2)}. Atual: ${totalItensMoedaBanco.value.toFixed(2)}`,
      type: 'warning'
    })
    return
  }

  if (!itensBaixa.value.some(i => i.isFake)) {
    emit('show-notification', { message: 'É obrigatório ter ao menos um item de amortização (Principal) na Composição do Movimento.', type: 'warning' })
    return
  }

  if (Number(item.valorAbatidoTitulo) > Number(props.tituloFinanceiro.saldoDevedor)) {
    emit('show-notification', {
      message: `Valor a abater (${Number(item.valorAbatidoTitulo).toFixed(2)}) não pode ser maior que o saldo devedor (${Number(props.tituloFinanceiro.saldoDevedor).toFixed(2)}).`,
      type: 'warning'
    })
    return
  }

  isLoading.value = true
  try {
    const payload = {
      idTituloFinanceiro: props.tituloFinanceiro.idTituloFinanceiro,
      idContaBancaria: item.idContaBancaria,
      valorAbatidoTitulo: Number(item.valorAbatidoTitulo),
      valorDescontoTitulo: item.valorDescontoMoedaTitulo ? Number(item.valorDescontoMoedaTitulo) : null,
      valorDescontoFinanceiroTitulo: item.valorDescontoFinanceiroMoedaTitulo ? Number(item.valorDescontoFinanceiroMoedaTitulo) : null,
      valorEfetivoMoedaBanco: Number(item.valorEfetivoMoedaBanco),
      dataPagamento: item.dataMovimento,
      lstFormaPagamento: item.lstFormaPagamento,
      lstMoedaBanco: dadosContaBancaria.moeda || '',
      itensAdicionais: itensBaixa.value
        .filter(i => !i.isFake)
        .map(i => ({
          idCategoriaFinanceira: i.idCategoriaFinanceira,
          valorMoedaBanco: i.valorMoedaBanco,
          valorMoedaPadrao: i.valorMoedaPadrao || 0,
          descricaoComplementar: i.descricaoComplementar || null
        }))
    }

    const res = await api.post('/MovimentacoesBancarias/baixa', payload)
    emit('show-notification', { message: 'Baixa registada com sucesso.', type: 'success' })
    emit('salvo', res.data)
    handleClose()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao registar baixa.'
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleClose = () => {
  // Clear form errors
  for (const key in formErrors.value) {
    delete formErrors.value[key]
  }

  // Reset form
  item.idContaBancaria = null
  item.nomeContaBancaria = ''
  item.dataMovimento = dataHojeBR()
  item.valorEfetivoMoedaBanco = null
  item.valorAbatidoTitulo = null
  item.valorDescontoMoedaTitulo = null
  item.valorDescontoFinanceiroMoedaTitulo = null
  item.lstFormaPagamento = ''
  itensBaixa.value = []
  dadosContaBancaria.apelido = ''
  dadosContaBancaria.banco = ''
  dadosContaBancaria.agencia = ''
  dadosContaBancaria.conta = ''
  dadosContaBancaria.moeda = ''
  emit('close')
}

const formatarNumeroView = (valor, decimais = 2) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(Number(valor))
}

// Detecta se o formulário foi alterado para controlar o hasChanges do ModalFormulario
const formAlterado = computed(() => {
  return !!(
    item.idContaBancaria ||
    item.valorEfetivoMoedaBanco ||
    item.valorAbatidoTitulo ||
    item.valorDescontoMoedaTitulo ||
    item.valorDescontoFinanceiroMoedaTitulo ||
    item.lstFormaPagamento ||
    itensBaixa.value.length > 0
  )
})

onMounted(async () => {
  // Só carrega listas se houver empresa ativa, senão aguarda o watch(isOpen) recarregar
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
  }
})
</script>

<template>
  <ModalFormulario
    :model-value="isOpen"
    titulo="Baixa Financeira"
    largura="modal-lg"
    textoConfirmar="Confirmar Baixa"
    :loading="isLoading || isLoadingListas"
    :has-changes="formAlterado"
    @confirmar="confirmarBaixa"
    @cancelar="handleClose"
    @update:model-value="!$event && handleClose()"
  >
    <!-- Informações do Título (readonly) -->
    <div class="card mb-3" v-if="tituloInfo">
      <div class="card-header bg-body-tertiary">
        <h6 class="mb-0"><i class="bi bi-file-text me-2 text-primary"></i> Dados do Título</h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-2">
            <div class="campo-exibicao">
              <label class="campo-label">Tipo Título</label>
              <span class="campo-valor">{{ tipoTituloTexto }}</span>
            </div>
          </div>
          <div class="col-md-3">
            <div class="campo-exibicao">
              <label class="campo-label">Fornecedor</label>
              <span class="campo-valor">{{ tituloInfo.nomeParceiro || '---' }}</span>
            </div>
          </div>
          <div class="col-md-1">
            <div class="campo-exibicao">
              <label class="campo-label">MOEDA</label>
              <span class="campo-valor">{{ siglaMoedaTitulo || '---' }}</span>
            </div>
          </div>
          <div class="col-md-2">
            <div class="campo-exibicao">
              <label class="campo-label">Valor Original</label>
              <span class="campo-valor">{{ formatarNumeroView(tituloInfo.valorTotal) }}</span>
            </div>
          </div>
          <div class="col-md-2">
            <div class="campo-exibicao">
              <label class="campo-label">Saldo Devedor</label>
              <span class="campo-valor fw-bold text-danger">{{ formatarNumeroView(tituloInfo.saldoDevedor) }}</span>
            </div>
          </div>
          <div class="col-md-2">
            <div class="campo-exibicao">
              <label class="campo-label">Vencimento</label>
              <span class="campo-valor">{{ formatarDataBR(tituloInfo.dataVencimento) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Split Payment — tributos que serão gerados nesta liquidação -->
    <div class="card mb-3 border-warning border-start border-4" v-if="tributosLiquidacao.length > 0">
      <div class="card-header bg-warning bg-opacity-10">
        <h6 class="mb-0 text-warning-emphasis">
          <i class="bi bi-graph-up-arrow me-2"></i> Split Payment — Tributos gerados nesta liquidação
        </h6>
      </div>
      <div class="card-body">
        <p class="small text-body-secondary mb-2">
          Ao confirmar a baixa, os tributos abaixo serão gerados automaticamente (Reforma Tributária):
        </p>
        <table class="table table-sm mb-0">
          <thead>
            <tr>
              <th>Tributo</th>
              <th>Credor</th>
              <th class="text-end">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in tributosLiquidacao" :key="t.idTributoRetidoPrevisto">
              <td>{{ getTributoTexto(t.lstTipoTributo) }}</td>
              <td>{{ t.nomeParceiroCredor || t.lstEsferaCredora || '---' }}</td>
              <td class="text-end">{{ formatarNumeroView(t.valorAArrecadar) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="fw-bold">
              <td colspan="2">Total</td>
              <td class="text-end">{{ formatarNumeroView(totalTributosLiquidacao) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Movimento Bancário - Informações do que se pagou/recebeu -->
    <div class="card mb-3">
      <div class="card-header bg-body-tertiary">
        <h6 class="mb-0"><i class="bi bi-bank me-2 text-primary"></i> Movimento Bancário - Informações do que se pagou/recebeu</h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-8">
            <CampoBusca
              id="baixaIdContaBancaria"
              label="Conta Bancária Origem/Destino"
              v-model="item.idContaBancaria"
              :configuracao-busca="configBuscaConta"
              required
              @selecionado="val => {
                item.nomeContaBancaria = val.descricao
                dadosContaBancaria.apelido = val.descricao
                dadosContaBancaria.banco = val.nomeInstituicaoFinanceira || ''
                dadosContaBancaria.agencia = val.agencia || ''
                dadosContaBancaria.conta = val.numeroConta || ''
                dadosContaBancaria.moeda = val.lstMoeda || ''
              }"
              @show-notification="p => emit('show-notification', p)"
            />
          </div>

          <div class="col-md-4">
            <CampoSelect
              id="baixaLstFormaPagamento"
              label="Forma de Pagamento"
              v-model="item.lstFormaPagamento"
              :options="listas.LstFormaPagamento || []"
              option-value="idListaClienteItem"
              option-label="textoLista"
              required
              :disabled="!item.idContaBancaria"
            />
          </div>

          <div class="col-md-4">
            <CampoInput
              id="baixaDataMovimento"
              label="Data Movimento"
              type="date"
              v-model="item.dataMovimento"
              required
              :disabled="!item.idContaBancaria"
            />
          </div>

          <div class="col-md-4">
            <label class="form-label is-required" for="baixaValorMovimentado">
              Valor Movimentado ({{ siglaMoedaConta }})
            </label>
            <div class="input-group-custom">
              <CampoInput
                id="baixaValorMovimentado"
                label="Valor Movimentado"
                type="moeda"
                v-model="item.valorEfetivoMoedaBanco"
                required
                :disabled="!item.idContaBancaria"
                hide-label
                class="input-group-field"
              />
              <button type="button" class="btn btn-outline-secondary btn-replicar input-group-btn"
                title="Replicar Saldo Devedor"
                :disabled="!moedasIguais || !item.idContaBancaria"
                @click="replicarSaldoDevedorMovimentado">
                <i class="bi bi-arrow-repeat me-1"></i> Devedor
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Composição do Movimento -->
    <div class="card mb-3 border-primary">
      <div class="card-header bg-primary text-white">
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0 text-white"><i class="bi bi-list-check me-2"></i> Composição do Movimento</h6>
          <div class="d-flex align-items-center gap-3">
            <div class="d-flex align-items-center gap-2">
              <div class="progress flex-grow-1" style="height: 8px; width: 120px;">
                <div class="progress-bar" role="progressbar"
                  :style="{ width: Math.min(100, percentualComposicao) + '%' }"
                  :class="[
                    excedeValorMovimentado ? 'bg-danger bar-excedida' : '',
                    !excedeValorMovimentado && percentualComposicao >= 100 ? 'bg-success bar-completa' : '',
                    !excedeValorMovimentado && percentualComposicao < 100 ? 'bar-parcial' : ''
                  ]">
                </div>
              </div>
              <small class="text-white fw-semibold">{{ percentualComposicao }}%</small>
            </div>
            <button type="button" class="btn btn-sm btn-light py-0"
              :disabled="!item.idContaBancaria"
              @click="abrirModalItem(-1)">
              <i class="bi bi-plus-lg"></i>
            </button>
          </div>
        </div>
        <div v-if="excedeValorMovimentado" class="alert alert-danger py-1 px-2 mb-0 mt-2 small">
          <i class="bi bi-exclamation-triangle-fill me-1"></i>
          Total da composição ({{ formatarNumeroView(totalItensMoedaBanco) }}) excede o Valor Movimentado ({{ formatarNumeroView(item.valorEfetivoMoedaBanco) }})
        </div>
      </div>
      <div class="card-body border-top border-secondary-subtle p-0">
        <div class="admin-table-container border-0 shadow-none mb-0">
          <table class="table admin-table m-0">
            <thead>
              <tr>
                <th>Categoria Financeira</th>
                <th>Descrição</th>
                <th class="text-end">Valor ({{ siglaMoedaConta }})</th>
                <th class="text-end" style="width:80px;">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(linha, index) in itensBaixa" :key="index">
                <td class="fw-medium">
                  <template v-if="linha.isFake">
                    <span class="badge bg-info text-dark">Principal - Amortização da Dívida</span>
                  </template>
                  <template v-else>
                    {{ linha.nomeCategoriaFinanceira || '-' }}
                  </template>
                </td>
                <td><small>{{ linha.descricaoComplementar || '-' }}</small></td>
                <td class="text-end fw-bold">{{ formatarNumeroView(linha.valorMoedaBanco) }}</td>
                <td class="text-end">
                  <button type="button" class="btn-icon text-primary" :disabled="!item.idContaBancaria" @click="abrirModalItem(index)"><i class="bi bi-pencil-fill"></i></button>
                  <button type="button" class="btn-icon text-danger" :disabled="!item.idContaBancaria || linha.isFake" @click="removerItem(index)"><i class="bi bi-trash-fill"></i></button>
                </td>
              </tr>
              <tr v-if="itensBaixa.length === 0">
                <td colspan="4" class="text-center text-muted py-4">
                  Nenhum item na Composição do Movimento. Clique em <i class="bi bi-plus-lg"></i> para adicionar itens.
                </td>
              </tr>
            </tbody>
            <tfoot v-if="itensBaixa.length > 0">
              <tr class="table-active fw-bold">
                <td colspan="2" class="text-end">TOTAIS:</td>
                <td class="text-end">{{ formatarNumeroView(totalItensMoedaBanco) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Relação com a dívida -->
    <div class="card mb-3">
      <div class="card-header bg-body-tertiary">
        <h6 class="mb-0"><i class="bi bi-link-45deg me-2 text-primary"></i> Relação Com a Dívida</h6>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="form-label" :class="{ 'is-required': !item.valorAbatidoTitulo && item.valorAbatidoTitulo !== 0 }" for="baixaValorAbater">
              Valor a abater da dívida ({{ siglaMoedaTitulo }})
            </label>
            <div class="input-group-custom">
              <CampoInput
                id="baixaValorAbater"
                label="Valor a abater da dívida"
                type="moeda"
                v-model="item.valorAbatidoTitulo"
                :disabled="moedasIguais || !item.idContaBancaria"
                required
                hide-label
                class="input-group-field"
              />
              <button type="button" class="btn btn-outline-secondary btn-replicar input-group-btn"
                title="Replicar Saldo Devedor"
                :disabled="moedasIguais || !item.idContaBancaria"
                @click="replicarSaldoDevedor">
                <i class="bi bi-arrow-repeat me-1"></i> Devedor
              </button>
            </div>
          </div>
          <div class="col-md-4">
            <CampoInput
              id="baixaValorDescontoComercial"
              :label="`Desconto no Produto (${siglaMoedaTitulo})`"
              type="moeda"
              v-model="item.valorDescontoMoedaTitulo"
              :disabled="!item.idContaBancaria"
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="baixaValorDescontoFinanceiro"
              :label="`Desconto Financeiro (${siglaMoedaTitulo})`"
              type="moeda"
              v-model="item.valorDescontoFinanceiroMoedaTitulo"
              :disabled="!item.idContaBancaria"
            />
          </div>
        </div>
      </div>
    </div>
  </ModalFormulario>

  <!-- MODAL PARA ADICIONAR/EDITAR ITEM DA COMPOSIÇÃO DO MOVIMENTO -->
  <ModalFormulario
    v-model="isModalItemOpen"
    titulo="Adicionar Item à Composição do Movimento"
    largura="modal-md"
    textoConfirmar="Salvar Item"
    @confirmar="salvarModalItem"
  >
    <div class="row g-3">
      <div class="col-md-12">
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" id="toggleAbaterDivida"
            v-model="abaterDivida"
            :disabled="jaTemItemFake || primeiroItem">
          <label class="form-check-label" for="toggleAbaterDivida">Abater da dívida</label>
        </div>
      </div>

      <div class="col-md-12">
        <CampoBusca id="idCategoriaFinanceira" label="Categoria Financeira"
          v-model="modalItemParaEdicao.idCategoriaFinanceira"
          :configuracao-busca="configBuscaCategoria"
          required
          :disabled="abaterDivida"
          @selecionado="val => { modalItemParaEdicao.nomeCategoriaFinanceira = val.nome; modalItemParaEdicao.codigoEstruturado = val.codigoEstruturado }"
          @show-notification="p => emit('show-notification', p)" />
      </div>

      <div class="col-md-4">
        <label class="form-label" :class="{ 'is-required': !modalItemParaEdicao.valorMoedaBanco }" for="modalValorRateio">
          Valor ({{ siglaMoedaConta }})
        </label>
        <div class="input-group-custom">
          <CampoInput id="modalValorRateio" label="Valor"
            type="moeda" v-model="modalItemParaEdicao.valorMoedaBanco"
            required hide-label class="input-group-field" />
          <button type="button" class="btn btn-outline-secondary btn-replicar input-group-btn"
            title="Replicar valor restante"
            @click="replicarValorRestante">
            <i class="bi bi-arrow-repeat me-1"></i> Valor Restante
          </button>
        </div>
      </div>

      <div class="col-md-8">
        <CampoInput id="descricaoComplementar" label="Descrição Complementar (Opcional)"
          type="text" v-model="modalItemParaEdicao.descricaoComplementar"
          placeholder="Ex: Taxa, Juros, Multa..." />
      </div>
    </div>
  </ModalFormulario>
</template>

<style scoped>
.campo-exibicao {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.campo-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color);
  font-weight: 600;
}

.campo-valor {
  font-size: 0.95rem;
  color: var(--bs-body-emphasis);
  font-weight: 500;
  word-break: break-word;
}

.card {
  background-color: var(--bs-tertiary-bg);
  border-radius: 0.375rem;
  border: 1px solid var(--bs-border-color-translucent) !important;
}

.card-header {
  background-color: rgba(52, 73, 94, 0.06);
  border-bottom: 1px solid var(--bs-border-color-translucent) !important;
  padding: 0.75rem 1.25rem;
}

.card-header h6 {
  color: var(--bs-heading-color);
  font-weight: 600;
  font-size: 0.95rem;
}

.card-body {
  padding: 1rem;
}

/* Input group custom: botão replicador colado ao campo input */
.input-group-custom {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem; /* Alinha com mb-3 do CampoInput */
}
.input-group-custom :deep(.campo-input-container) {
  flex: 1;
  margin-bottom: 0 !important;
}
.input-group-custom :deep(.campo-input-container .form-control) {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.input-group-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 0;
  white-space: nowrap;
  display: flex;
  align-items: center;
  height: calc(1.5em + 0.75rem + 2px); /* mesma altura do form-control padrão */
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
}

/* Botões replicadores com estado disabled */
.btn-replicar:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  filter: grayscale(0.8);
  border-style: dashed;
}

/* Botões de ação no grid quando disabled */
.btn-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  filter: grayscale(0.9);
}

/* Asterisco vermelho para labels manuais de campos obrigatórios (Valor Movimentado, Valor a abater, Valor do modal) */
.form-label.is-required::after {
  content: " *";
  color: #dc3545;
  font-weight: bold;
}

/* Barra de progresso: quando parcial, borda branca para contrastar com o header bg-primary */
.bar-parcial {
  border-right: 2px solid #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
}
.bar-completa {
  border: none;
  box-shadow: none;
}
</style>
